// Universal behavior tracking — works for guests AND signed-in users
// Events are sent to the track-event edge function which inserts into behavior_events table

import { detectLocation } from "@/lib/geolocation";

const VISITOR_KEY = "dos-visitor-id";

function getOrCreateVisitorId(): string {
  let id = localStorage.getItem(VISITOR_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(VISITOR_KEY, id);
  }
  return id;
}

// One session_id per page load
const SESSION_ID = crypto.randomUUID();

let cachedDeviceInfo: Record<string, unknown> | null = null;
function getDeviceInfo(): Record<string, unknown> {
  if (cachedDeviceInfo) return cachedDeviceInfo;
  cachedDeviceInfo = {
    userAgent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    referrer: document.referrer || null,
    language: navigator.language,
  };
  return cachedDeviceInfo;
}

let cachedGeo: { country?: string; city?: string } | null = null;

export async function trackBehavior(
  eventType: string,
  eventData: Record<string, unknown> = {},
  userId?: string | null,
) {
  try {
    // Lazy-load geo once
    if (!cachedGeo) {
      try {
        const geo = await detectLocation();
        cachedGeo = { country: geo.countryCode, city: geo.city };
      } catch {
        cachedGeo = {};
      }
    }

    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
    if (!projectId) return;

    const url = `https://${projectId}.supabase.co/functions/v1/track-event`;

    // Fire and forget — don't block UI
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        session_id: SESSION_ID,
        visitor_id: getOrCreateVisitorId(),
        user_id: userId || null,
        event_type: eventType,
        event_data: eventData,
        device_info: getDeviceInfo(),
        country: cachedGeo?.country || null,
        city: cachedGeo?.city || null,
      }),
      keepalive: true,
    }).catch(() => {
      // Silently fail — tracking should never break the app
    });
  } catch {
    // Silently fail
  }
}

// Convenience helpers
export const trackSearch = (query: string, category?: string, userId?: string | null) =>
  trackBehavior("search", { query, category }, userId);

export const trackClarificationAnswer = (question: string, answer: string, userId?: string | null) =>
  trackBehavior("clarification_answer", { question, answer }, userId);

export const trackDecisionShown = (query: string, winnerName: string, confidence: string, userId?: string | null) =>
  trackBehavior("decision_shown", { query, winner: winnerName, confidence }, userId);

export const trackClick = (productName: string, seller: string, query: string, userId?: string | null) =>
  trackBehavior("click", { product: productName, seller, query }, userId);

export const trackSave = (productName: string, query: string, userId?: string | null) =>
  trackBehavior("save", { product: productName, query }, userId);

export const trackFeedback = (query: string, value: string, meta?: Record<string, string | undefined> | string | null) => {
  const extraData = typeof meta === "object" && meta !== null ? meta : {};
  trackBehavior("feedback", { query, value, ...extraData });
};

export const trackSignup = (userId: string) =>
  trackBehavior("signup", {}, userId);

export const trackUpgrade = (plan: string, userId: string) =>
  trackBehavior("upgrade", { plan }, userId);

export const trackRouteClick = (product: string, seller: string, routeType: string, userId?: string | null) =>
  trackBehavior("route_click", { product, seller, routeType }, userId);

export const trackHomepageSearchStarted = (category: string | undefined, signedIn: boolean, sourceSurface: string) =>
  trackBehavior("homepage_search_started", { category, signed_in: signedIn, source_surface: sourceSurface });

export const trackPlanSelected = (selectedPlan: string, sourceSurface: string, signedIn: boolean) =>
  trackBehavior("plan_selected", { selected_plan: selectedPlan, source_surface: sourceSurface, signed_in: signedIn });

export const trackPostResultSecondSearch = (originalCategory: string | undefined, selectedFollowup: string, signedIn: boolean) =>
  trackBehavior("post_result_second_search", { original_category: originalCategory, selected_followup: selectedFollowup, signed_in: signedIn });
